<template>
  <view class="pft-table-component__row" :class="rowClass" :style="rowStyle">
    <view class="pft-table-component__row__items">
      <view
        class="pft-table-component__body__cell"
        v-for="(column, index) in columns"
        :key="`index_${index}`"
        :class="itemClass(index, row.rank as number)"
        :style="itemStyle(index, 0)"
        :ref="(el: any) => checkRef(el, column.key, row)"
      >
        <view v-if="row.overflow?.[column.key]" class="hover-item">
          <view class="hover-arrow" />
          <component v-if="column.tpl" :is="renderTpl(column.tpl, row, itemStyle(index, 0))"></component>
          <view v-else>{{ getVal(row, column.key) }}</view>
        </view>
        <template v-if="column?.type === 'rank'">
          <template v-if="theme === 'default'">
            <view v-if="row.rank === 1 && (!column.rankType || (column.rankType && column.rankType === 'crown'))" class="rank img">
              <img :src="icon.crown2" />
            </view>
            <view v-else class="rank">
              {{ row.rank }}
            </view>
          </template>
          <template v-else-if="['data-percent', 'crown123'].some(t => t === theme)">
            <view v-if="[1, 2, 3].indexOf(row.rank as number) >= 0" class="rank img">
              <img :src="icon[`crown${row.rank}`]" />
            </view>
            <view v-else class="rank">
              {{ row.rank.toString().padStart(2, "0") }}
            </view>
          </template>
        </template>
        <template v-else-if="column?.type === 'compare'">
          <template v-if="getVal(row, column.key) === 0">
            <text class="text">--</text>
          </template>
          <template v-else>
            <text class="text">{{ Math.abs(Number(getVal(row, column.key))) }}%</text>
            <img class="upanddown" :src="Number(getVal(row, column.key)) > 0 ? upIcon : downIcon" />
          </template>
        </template>
        <template v-else-if="column?.type === 'progress'">
          <Progress :rank="row.rank" :data="Number(getVal(row, column.key))" :style="column?.progressStyle"></Progress>
        </template>
        <template v-else>
          <component v-if="column.tpl" :is="renderTpl(column.tpl, row, itemStyle(index, 0))"></component>
          <text class="text" v-else-if="column.format"> {{ formatData(getVal(row, column.key), column.format) }}</text>
          <text class="text" v-else>{{ getVal(row, column.key) }}</text>
        </template>
      </view>
      <template v-for="(column, index) in columns" :key="`compare_${index}`">
        <view
          class="pft-table-component__body__cell"
          v-if="index > compareStartColIndex - 1"
          :class="itemClass(index, row.rank as number)"
          :style="itemStyle(index, 0, true)"
          :ref="(el: any) => checkRef(el, column.key, compareRow)"
        >
          <view v-if="compareRow?.overflow?.[column.key]" class="hover-item">
            <view class="hover-arrow" />
            <component v-if="column.tpl" :is="renderTpl(column.tpl, compareRow, itemStyle(index, 0))"></component>
            <view v-else>{{ getVal(compareRow, column.key) }}</view>
          </view>
          <template v-if="column?.type === 'rank'">
            <template v-if="theme === 'default'">
              <view v-if="row.rank === 1 && (!column.rankType || (column.rankType && column.rankType === 'crown'))" class="rank img">
                <img :src="icon.crown2" />
              </view>
              <view v-else class="rank">
                {{ row.rank }}
              </view>
            </template>
            <template v-else-if="['data-percent', 'crown123'].some(t => t === theme)">
              <view v-if="[1, 2, 3].indexOf(row.rank as number) >= 0" class="rank img">
                <img :src="icon[`crown${row.rank}`]" />
              </view>
              <view v-else class="rank">
                {{ row.rank.toString().padStart(2, "0") }}
              </view>
            </template>
          </template>
          <template v-else-if="column?.type === 'compare'">
            <template v-if="getVal(compareRow, column.key) === 0">
              <text class="text">--</text>
            </template>
            <template v-else>
              <text class="text">{{ Math.abs(Number(getVal(compareRow, column.key))) }}%</text>
              <img class="upanddown" :src="Number(getVal(compareRow, column.key)) > 0 ? upIcon : downIcon" />
            </template>
          </template>
          <template v-else-if="column?.type === 'progress'">
            <Progress :rank="row.rank" :data="Number(getVal(compareRow, column.key))" :style="column?.compareProgressStyle"></Progress>
          </template>
          <template v-else>
            <component v-if="column.tpl" :is="renderTpl(column.tpl, compareRow, itemStyle(index, 0))"></component>
            <text class="text" v-else-if="column.format"> {{ formatData(getVal(compareRow, column.key), column.format) }}</text>
            <text class="text" v-else>{{ getVal(compareRow, column.key) }}</text>
          </template>
        </view>
      </template>
    </view>
    <!-- 待完善 -->
    <Progress v-if="theme === 'data-percent'" :rank="row.rank" :data="row.percent"></Progress>
  </view>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch } from "vue"
import Progress from "./progress.vue"
import upIcon from "@p-v/asset/image/icon_up@2x.png"
import downIcon from "@p-v/asset/image/icon_down@2x.png"
import { hideClass, showClass } from "../fade.hook"
const props = defineProps<{
  row: any
  compareRow: any
  rowIndex: number
  columns: any[]
}>()

const rows = inject("rows") as number
const rowClass = ref<{ [key: string]: string | number | boolean }>({})
const rowStyle = ref<{ [key: string]: string }>({})
const parentItemClass = inject("itemClass") as (index: number, rank: number) => object
const parentItemStyle = inject("itemStyle") as (index: number, rowIndex: number) => object
const checkRef = inject("checkRef") as (el: any, key: string, row: any) => void
const getVal = inject("getVal") as (row: any, key: string) => string | number
const renderTpl = inject("renderTpl") as (tpl: string, row: any, style: any) => any
const formatData = inject("formatData") as (value: any, format: string) => string
const compareStartColIndex = inject("compareStartColIndex") as number
const theme = inject("theme") as string
const icon = inject("icon") as { [key: string]: string }

const isLastRow = ref<boolean>(false)
const separated = inject("separated") as boolean
const rowStyleFromProps = inject("rowStyle") as { [key: string]: string }
const seperatedStyle = inject("seperatedStyle") as { [key: string]: string }

const itemClass = (index: number, rank: number) => {
  return {
    ...parentItemClass(index, rank),
    "pft-table-component__body__cell__expand": index < compareStartColIndex,
  }
}

const itemStyle = (index: number, rowIndex: number, compare?: boolean): any => {
  const style = parentItemStyle(index, rowIndex)
  if (index < compareStartColIndex) {
    style["grid-row-start"] = 1
    style["grid-row-end"] = 3
    style["height"] = "var(--MAX-ROW-ALL-HEIGHT, 64px)"
    style["line-height"] = "var(--MAX-ROW-ALL-HEIGHT, 64px)"
  }
  if (compare) {
    style["align-self"] = "start"
  } else {
    style["align-self"] = "end"
  }

  return style
}

if (rows !== undefined) {
  if (props.rowIndex === rows) {
    watch(
      () => hideClass.value,
      () => {
        rowClass.value = showClass.value
      },
      { immediate: true, deep: true },
    )
    isLastRow.value = true
  } else if (props.rowIndex === 0) {
    watch(
      () => showClass.value,
      () => {
        rowClass.value = hideClass.value
      },
      { immediate: true, deep: true },
    )
  }
}

watch(
  () => isLastRow.value,
  () => {
    if (isLastRow.value) {
      rowStyle.value = Object.assign(rowStyle.value, {
        height: "0px",
        overflow: "hidden",
      })
    } else {
      if (separated && props.rowIndex % 2 === 0) {
        rowStyle.value = Object.assign(rowStyle.value, seperatedStyle)
      } else {
        rowStyle.value = Object.assign(rowStyle.value, rowStyleFromProps)
      }
    }
  },
  { immediate: true, deep: true },
)
</script>

<style lang="scss" scoped>
.pft-table-component__row {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: var(--GRID-ROW-TEMPLATE-ROWS) var(--GRID-ROW-TEMPLATE-ROWS);
  height: var(--MAX-ROW-ALL-HEIGHT, 64px);
  // overflow: hidden;
}

.pft-table-component__row__items {
  display: grid;
  grid-template-columns: var(--GRID-ROW-TEMPLATE-COLUMNS);
}
.pft-table-component__body__cell__expand {
  grid-row-start: 1;
  grid-row-end: 3;
}
</style>
